import React, { useState, useMemo, useEffect } from 'react';
import { Divider, Flex, Switch, Typography } from 'antd';
import { Patient } from '@models/patient';
import {
  PatientSearchForm,
  SCORE_AT_LEAST_1,
  SearchFormValues,
  LatestScoreFilterValue,
} from './form/PatientSearchForm';
import { PatientList } from './PatientList';
import { multiFieldSort } from '../../../utils/sort';
import { PatientCareLevel } from '../../../constants/careLevel';
import { IndependentLifeLevel } from '../../../constants/independentLifeLevel';
import { extractPatientTags } from '../../../utils/patient-tags';
import { getOhatScoreTotal } from '../../../utils/ohat-score-utils';
import { useTranslation } from 'react-i18next';

/**
 * 最新（直近）のOHAT評価を取得する。
 * 一覧API（organization/:id/patients）は ohatScores を evaluatedAt の降順で返すため、
 * 先頭の要素が最新の評価となる。
 */
const getLatestOhatScore = (patient: Patient) => patient.ohatScores?.[0];

/** 最新評価の合計点（OHATスコア合計）。評価が1件も無い場合は undefined。 */
const getLatestTotalScore = (patient: Patient): number | undefined => {
  const latest = getLatestOhatScore(patient);
  if (!latest) return undefined;
  return getOhatScoreTotal(latest);
};

/**
 * 項目別スコアの一致判定。
 * 未指定（null/undefined）は常に一致、'1以上' は score >= 1、0/1/2 は完全一致。
 */
const matchesItemScore = (
  score: number,
  condition: LatestScoreFilterValue | undefined,
): boolean => {
  if (condition == null) return true;
  if (condition === SCORE_AT_LEAST_1) return score >= 1;
  return score === condition;
};

export type Props = {
  patients: Patient[];
  showSearchForm: boolean;
  setShowSearchForm: (show: boolean) => void;
  onFilteredPatientsChange?: (patients: Patient[]) => void;
};

export const PatientListPanel: React.FC<Props> = ({
  patients,
  showSearchForm,
  setShowSearchForm,
  onFilteredPatientsChange,
}) => {
  const { t } = useTranslation();
  const [showArchived, setShowArchived] = useState(false);
  const [filteredAndSortedPatients, setFilteredAndSortedPatients] = useState<
    Patient[]
  >([]);

  const filteredFinal = useMemo(() => {
    return filteredAndSortedPatients.filter(
      (v) => showArchived || !v.archivedAt,
    );
  }, [filteredAndSortedPatients, showArchived]);

  useEffect(() => {
    if (onFilteredPatientsChange) {
      onFilteredPatientsChange(filteredFinal);
    }
  }, [filteredFinal, onFilteredPatientsChange]);

  useEffect(() => {
    setFilteredAndSortedPatients(patients);
  }, [patients]);

  const handleSearch = (values: SearchFormValues) => {
    let filtered = patients.filter((patient) => {
      const tagIds = extractPatientTags(patient).map((t) => t.id);
      return (
        (!values.name || patient.name.includes(values.name)) &&
        (!values.gender || patient.gender === values.gender) &&
        (!values.careLevel || patient.careLevel === values.careLevel) &&
        (!values.lifeLevel ||
          patient.independentLifeLevel === values.lifeLevel) &&
        (!values.dentalIntervention ||
          patient.dentalIntervention === values.dentalIntervention) &&
        (!values.tagIds?.length ||
          values.tagIds.every((id) => tagIds.includes(id)))
      );
    });

    // 評価期間によるフィルタリング（過去の評価履歴のいずれかが期間内にあるか）
    if (values.evaluationDate && values.evaluationDate.length === 2) {
      const start = values.evaluationDate[0].startOf('day').toDate();
      const end = values.evaluationDate[1].endOf('day').toDate();

      filtered = filtered.filter((patient) => {
        if (!patient.ohatScores || patient.ohatScores.length === 0)
          return false;
        return patient.ohatScores.some((score) => {
          const evalDate = new Date(score.evaluatedAt);
          return evalDate >= start && evalDate <= end;
        });
      });
    }

    // 項目別のOHATスコアでフィルタリング（最新のOHATスコアを参照する）
    const itemScoreConditions = [
      values.latestScoreQ1,
      values.latestScoreQ2,
      values.latestScoreQ3,
      values.latestScoreQ4,
      values.latestScoreQ5,
      values.latestScoreQ6,
      values.latestScoreQ7,
      values.latestScoreQ8,
    ];
    // 指定された条件がいずれも無い場合はフィルタしない
    if (itemScoreConditions.some((condition) => condition != null)) {
      filtered = filtered.filter((patient) => {
        const latestOhatScore = getLatestOhatScore(patient);

        if (!latestOhatScore) {
          return false; // 条件が指定されているのにスコアが1つもない患者は除外
        }

        const itemScores = [
          latestOhatScore.q1,
          latestOhatScore.q2,
          latestOhatScore.q3,
          latestOhatScore.q4,
          latestOhatScore.q5,
          latestOhatScore.q6,
          latestOhatScore.q7,
          latestOhatScore.q8,
        ];

        return itemScoreConditions.every((condition, index) =>
          matchesItemScore(itemScores[index], condition),
        );
      });
    }

    // 合計点でフィルタリング（点数未入力なら条件は無視する）
    if (values.latestTotalScore != null) {
      const threshold = values.latestTotalScore;
      const condition = values.latestTotalScoreCondition ?? 'eq';

      filtered = filtered.filter((patient) => {
        const totalScore = getLatestTotalScore(patient);

        if (totalScore == null) {
          return false; // 条件が指定されているのに評価が1件もない患者は除外
        }

        if (condition === 'gte') return totalScore >= threshold;
        if (condition === 'lte') return totalScore <= threshold;
        return totalScore === threshold;
      });
    }

    filtered = filtered.map((patient) => ({
      ...patient,
      // 一覧APIは合計点を返さないため、最新評価の q1..q8 から算出してソートに使う
      latestScore: getLatestTotalScore(patient),
      latestOhatScoreEvaluatedAtForSort:
        patient.latestOhatScoreEvaluatedAt?.split('T')[0],
      careLevelForSort: PatientCareLevel.getByKey(patient.careLevel).sortOrder,
      independentLifeLevelForSort: IndependentLifeLevel.getByKey(
        patient.independentLifeLevel,
      ).sortOrder,
    }));

    // ソート条件を取得し、優先度順に並べ替え
    const ascSortFields =
      values.ascSort?.map((v) => ({ ...v, type: 'asc' as const })) || [];
    const descSortFields =
      values.descSort?.map((v) => ({ ...v, type: 'desc' as const })) || [];
    const sortFields = [...ascSortFields, ...descSortFields].sort(
      (a, b) => a.order - b.order,
    );
    const sorted = multiFieldSort(filtered, sortFields);
    setFilteredAndSortedPatients(sorted);
  };

  const resetSearch = () => {
    setFilteredAndSortedPatients(patients);
  };

  const patientData = useMemo(() => {
    return (
      filteredAndSortedPatients
        // アーカイブ済みはデフォルト非表示。トグルONのときのみ含める。
        .filter((v) => showArchived || !v.archivedAt)
        .map((v) => ({
          key: v.id,
          id: v.id,
          name: v.name,
          age: v.age,
          gender: v.gender,
          careLevel: v.careLevel,
          lifeLevel: v.independentLifeLevel,
          dentalIntervention: v.dentalIntervention,
          latestOhatScoreEvaluatedAt: v.latestOhatScoreEvaluatedAt,
          // 最新評価の合計点（評価が1件も無ければ undefined。一覧では非表示にする）
          latestTotalScore: getLatestTotalScore(v),
          ohatScoreEvaluatedCount: v.ohatScoreEvaluatedCount ?? 0,
          // 全評価に添付された写真の合計枚数（0枚なら一覧にカメラマークを出さない）
          photoCount: (v.ohatScores ?? []).reduce(
            (sum, s) => sum + (s.photoCount ?? 0),
            0,
          ),
          tags: extractPatientTags(v),
          archivedAt: v.archivedAt,
        }))
    );
  }, [filteredAndSortedPatients, showArchived]);

  return (
    <>
      <div style={{ overflow: 'hidden' }}>
        {showSearchForm && (
          <div
            style={{
              animation: showSearchForm
                ? 'slideDown 0.3s ease-out'
                : 'slideUp 0.3s ease-out',
              transformOrigin: 'top',
            }}
          >
            <PatientSearchForm
              onSearch={handleSearch}
              onReset={resetSearch}
              onClose={() => setShowSearchForm(false)}
            />
            <Divider />
          </div>
        )}
        <style>
          {`
            @keyframes slideDown {
              from {
                transform: scaleY(0);
                opacity: 0;
              }
              to {
                transform: scaleY(1);
                opacity: 1;
              }
            }
            @keyframes slideUp {
              from {
                transform: scaleY(1);
                opacity: 1;
              }
              to {
                transform: scaleY(0);
                opacity: 0;
              }
            }
          `}
        </style>
      </div>
      <Flex
        justify="flex-end"
        align="center"
        gap={8}
        style={{ marginBottom: 8 }}
      >
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('patient.showArchived')}
        </Typography.Text>
        <Switch
          size="small"
          checked={showArchived}
          onChange={setShowArchived}
        />
      </Flex>
      <PatientList patients={patientData} />
    </>
  );
};
